import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { AccordianComponent } from '@app/shared/custom-component/accordian/accordian.component';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { DataGridVO } from '@app/shared/custom-component/data-grid/data-grid.model';
import { HttpService } from '@core/services/http.service';
import { take } from 'rxjs';
import { forkJoin } from 'rxjs';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';

interface LEAReport {
  title: string;
  school_year: string | null;
  progress: number;
  status: string;
  updatedDate: string | null;
  showDownload: boolean;
}

@Component({
  selector: 'app-lea-reports',
  standalone: true,
  imports: [AccordianComponent, DataGridComponent,MatTooltipModule],
  templateUrl: './lea-reports.component.html',
  styleUrls: ['./lea-reports.component.scss']
})
export class LEAReportsComponent implements OnInit, AfterViewInit {
  @ViewChild(AccordianComponent) accordion?: AccordianComponent;
  @ViewChild(DataGridComponent) reportGrid?: DataGridComponent;

  constructor(private cdr: ChangeDetectorRef, private httpService: HttpService) {}

  leaReportData: LEAReport[] = [];
  schoolYears: any[] = [];

  leaReportGridConfig: DataGridVO = new DataGridVO({
    role: 'reports',
    activeTab: 'lea-reports',
    customHeaders: [
      { dataField: 'title', caption: 'Title',  allowFiltering: true, allowSearch: { isSearchEnable: true, type: 'string' }, cssClass: 'source-wrap-column' },
      { dataField: 'school_year', caption: 'School Year', allowFiltering: true, allowSearch: { isSearchEnable: true, type: 'string' } },
      { dataField: 'progress', caption: 'Progress', allowFiltering: true, allowSearch: { isSearchEnable: true, type: 'string' } },
      { dataField: 'status', caption: 'Status', allowFiltering: true, allowSearch: { isSearchEnable: true, type: 'string' } },
      { dataField: 'updatedDate', caption: 'Updated Date', allowFiltering: true, allowSearch: { isSearchEnable: true, type: 'date' }, dataType: 'date' }
    ],
    showActionsColumn: true,
    showQueueActions: false,
    showFiltering: true,
    showSearchRow: true,
    showPaging: true,
    customPageSize: 10,
    customPageSizes: [10, 25, 50],
    customNoDataText: 'No reports found',
    data: this.leaReportData
  });

  ngOnInit(): void {
    this.fetchReportData();
  }

  private fetchReportData(): void {
    forkJoin({
      reports: this.httpService.post(API_ENDPOINTS.REPORTS, { tab: 'LEA' }).pipe(take(1)),
      schoolYears: this.httpService.get(API_ENDPOINTS.lookupSchoolYears).pipe(take(1))
    }).subscribe({
      next: ({ reports, schoolYears }: any) => {
        this.schoolYears = schoolYears?.data || [];
        const reportData = Array.isArray(reports?.data?.data) ? reports.data.data : Array.isArray(reports?.data) ? reports.data : Array.isArray(reports) ? reports : [];
        this.leaReportData = reportData.map((item: any) => {
          const match = this.schoolYears.find((sy: any) => String(sy.key) === String(item.school_year));
          return {
            ...item,
            title: item.title ?? '---',
            school_year: match ? match.value : (item.school_year ?? '---'),
            progress: item.progress != null ? `${item.progress}` : '---',
            status: item.status ?? '---',
            updatedDate: item.updatedDate ?? '---'
          };
        });
        this.leaReportGridConfig = this.leaReportGridConfig.updateConfig({ data: this.leaReportData });
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        console.error('Failed to fetch LEA reports:', err);
      }
    });
  }


  downloadReport(rowData: any): void {
    const item = rowData;
    const fileName = item.title || 'LEA';
    const url = API_ENDPOINTS.DATA_TEMPLATES.DOWNLOAD_TEMPLATES;
    const identity_id = item.id;
    const contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    // Step 1: Download the template
    this.httpService.getDownload1(url + '/' + item.data_template_col_sel_ids + '/' + item.template_id + '/' + identity_id + '/' + false + '/' + true, contentType).subscribe({
      next: async (blob: Blob) => {
        // Step 2: Load template into ExcelJS and fill row_data
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const worksheet = workbook.getWorksheet(1);
        if (!worksheet) {
          console.error('No worksheet found in template');
          return;
        }

        const rawData = item.row_data;
        const normalize = (row: any) =>
          JSON.stringify(Object.keys(row).sort().reduce((acc: any, k: string) => {
            acc[k] = row[k] == null ? '' : String(row[k]).trim();
            return acc;
          }, {}));
        const data = Array.isArray(rawData)
          ? rawData.filter((row: any, index: number, arr: any[]) => {
              const key = normalize(row);
              return arr.findIndex((r: any) => normalize(r) === key) === index;
            })
          : rawData;
        if (Array.isArray(data) && data.length > 0) {
          const headers = Object.keys(data[0]);

          // Find the header row (first row with content) to get column mapping
          let headerRowIndex = 1;
          const firstRow = worksheet.getRow(1);
          const columnMap: { [key: string]: number } = {};

          firstRow.eachCell((cell: any, colNumber: number) => {
            const cellValue = cell.value?.toString()?.trim();
            if (cellValue && headers.includes(cellValue)) {
              columnMap[cellValue] = colNumber;
            }
          });

          // If no matching headers found in row 1, use headers from data as columns
          if (Object.keys(columnMap).length === 0) {
            headers.forEach((header, index) => {
              columnMap[header] = index + 1;
              worksheet.getRow(1).getCell(index + 1).value = header;
            });
            worksheet.getRow(1).font = { bold: true };
            headerRowIndex = 1;
          }

          // Clear existing data rows from template to avoid duplicates
          const totalRows = worksheet.rowCount;
          if (totalRows > headerRowIndex) {
            worksheet.spliceRows(headerRowIndex + 1, totalRows - headerRowIndex);
          }

          // Step 3: Write data rows starting after header
          const startRow = headerRowIndex + 1;
          data.forEach((row: any, rowIndex: number) => {
            const excelRow = worksheet.getRow(startRow + rowIndex);
            headers.forEach(header => {
              const colNum = columnMap[header];
              if (colNum) {
                excelRow.getCell(colNum).value = row[header] ?? '';
              }
            });
            excelRow.commit();
          });
        }

        // Step 4: Save and download
        const buffer = await workbook.xlsx.writeBuffer();
        FileSaver.saveAs(new Blob([buffer]), `${fileName}.xlsx`);
      },
      error: (err: any) => {
        console.error('Failed to download report:', err);
      }
    });
  }
  

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.accordion) {
        const originalToggle = this.accordion.toggle.bind(this.accordion);
        this.accordion.toggle = () => {
          originalToggle();
          if (this.accordion?.isOpen) {
            this.refreshGridLayout();
          }
        };
        this.accordion.isOpen = true;
      }
      this.cdr.detectChanges();
    });
  }

  private refreshGridLayout(): void {
    requestAnimationFrame(() => {
      this.reportGrid?.dataGrid?.instance?.updateDimensions();
      this.reportGrid?.dataGrid?.instance?.repaint();
    });
  }
}
